import { useState, useMemo, useCallback } from "react";
import { Check, Lock, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import type { VideoStyle } from "../types/videoStudio.types";
import { VIDEO_STYLES } from "../types/videoStudio.types";

type VideoTab = "all" | "sassy" | "animated" | "medical" | "ugc" | "modeling" | "podcast" | "demo";

const VIDEO_TAB_IDS: VideoTab[] = ["all", "animated"];
const VIDEO_TAB_KEYS: Record<VideoTab, string> = {
  all: "video-tab-all",
  sassy: "video-tab-sassy",
  animated: "video-tab-medical",
  medical: "video-tab-medical",
  ugc: "video-tab-ugc",
  modeling: "video-tab-modeling",
  podcast: "video-tab-podcast",
  demo: "video-tab-demo",
};

const STYLE_TO_TAB: Record<string, VideoTab> = {
  "sassy-object": "animated",
  "animated-problem": "animated",
  "medical-3d": "medical",
  "ugc-testimonial": "ugc",
  "product-modeling": "modeling",
  "podcast-style": "podcast",
  "product-demo": "demo",
};

interface VideoStudioGalleryProps {
  selectedStyleId: string | null;
  onSelectStyle: (style: VideoStyle) => void;
}

export function VideoStudioGallery({ selectedStyleId, onSelectStyle }: VideoStudioGalleryProps) {
  const [activeTab, setActiveTab] = useState<VideoTab>("all");
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const { language } = useAuth();

  const handleImageLoad = useCallback((id: string) => {
    setLoadedImages((prev) => new Set(prev).add(id));
  }, []);

  const filteredStyles = useMemo(() => {
    const enabled = VIDEO_STYLES.filter((s) => s.enabled);
    if (activeTab === "all") return enabled;
    return enabled.filter((s) => STYLE_TO_TAB[s.id] === activeTab);
  }, [activeTab]);

  return (
    <div className="h-full min-h-0 overflow-y-auto p-5">
      {/* Selection banner */}
      {selectedStyleId ? (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#EEF0FF] px-4 py-2.5">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-fluxi text-white text-xs font-bold flex-shrink-0">
            1
          </div>
          <p className="text-sm text-fluxi font-medium">
            1 {dictionary["video-style-selected"][language as Language]}
          </p>
          <span className="ml-auto text-sm font-bold text-fluxi tabular-nums">1/1</span>
        </div>
      ) : null}

      {/* Tab pills */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide whitespace-nowrap pb-1">
          {VIDEO_TAB_IDS.map((tabId) => (
            <button
              key={tabId}
              type="button"
              onClick={() => setActiveTab(tabId)}
              className={cn(
                "px-4 py-1.5 rounded-full text-sm font-medium transition-all",
                activeTab === tabId
                  ? "bg-foreground text-background"
                  : "bg-white border border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              )}
            >
              {dictionary[VIDEO_TAB_KEYS[tabId]][language as Language]}
            </button>
          ))}
        </div>
      </div>

      {/* Grid layout */}
      <div className="grid grid-cols-3 gap-3">
        {filteredStyles.map((style) => {
          const isSelected = selectedStyleId === style.id;
          const isDisabled = !style.enabled;
          const canToggle = !isDisabled;

          return (
            <div
              key={style.id}
              role="button"
              tabIndex={0}
              onClick={() => canToggle && onSelectStyle(style)}
              onKeyDown={(e) => e.key === "Enter" && canToggle && onSelectStyle(style)}
              className={cn(
                "relative group",
                "rounded-xl overflow-hidden transition-all duration-200",
                isSelected
                  ? "ring-2 ring-primary ring-offset-2"
                  : canToggle
                    ? "hover:scale-[1.02] cursor-pointer"
                    : "opacity-40 cursor-not-allowed"
              )}
            >
              {/* Thumbnail */}
              <div
                className={cn(
                  "w-full aspect-[4/5] flex flex-col items-center justify-center relative",
                  isDisabled
                    ? "bg-gray-200"
                    : !style.thumbnail
                      ? "bg-gradient-to-br from-[#1a1a3e] to-[#2d1b69]"
                      : ""
                )}
              >
                {style.thumbnail && !isDisabled && (
                  <>
                    {!loadedImages.has(style.id) && (
                      <div className="absolute inset-0 bg-muted animate-pulse" />
                    )}
                    <img
                      src={style.thumbnail}
                      alt={dictionary[style.nameKey]?.[language as Language] ?? style.nameKey}
                      className={cn(
                        "absolute inset-0 w-full h-full object-cover transition-opacity duration-300",
                        loadedImages.has(style.id) ? "opacity-100" : "opacity-0"
                      )}
                      onLoad={() => handleImageLoad(style.id)}
                    />
                  </>
                )}
                {isDisabled ? (
                  <Lock className="h-8 w-8 text-gray-400" />
                ) : !style.thumbnail ? (
                  <Play className="h-10 w-10 text-white/80" />
                ) : null}

                {/* Style name overlay */}
                <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent">
                  <p className="text-[11px] font-semibold text-white truncate">
                    {dictionary[style.nameKey]?.[language as Language] ?? style.nameKey}
                  </p>
                  {style.badgeKey && (
                    <span
                      className={cn(
                        "inline-block mt-0.5 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wide",
                        style.badgeKey === "new"
                          ? "bg-green-500/80 text-white"
                          : "bg-white/20 text-white/70"
                      )}
                    >
                      {dictionary[style.badgeKey]?.[language as Language] ?? style.badgeKey}
                    </span>
                  )}
                </div>
              </div>

              {/* Selected checkmark — bottom left, same as StyleCard */}
              {isSelected && (
                <div className="absolute bottom-2 left-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center z-10 shadow-md">
                  <Check className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={3} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredStyles.length === 0 && (
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          <p className="text-sm">{dictionary["video-no-styles-found"][language as Language]}</p>
        </div>
      )}
    </div>
  );
}
